(() => {
  if (customElements.get('vitablue-cart-aov')) return;

  const root = window.Shopify?.routes?.root || '/';
  const parse = (value) => {
    try { return typeof value === 'string' ? JSON.parse(value) : value; } catch { return null; }
  };
  const metadata = (item) => parse(item.properties?.__rapi_offer) || {};
  const signature = (cart) => JSON.stringify([cart.currency, cart.total_price, cart.items, cart.discount_codes]);
  const readCart = async () => {
    const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  };
  const money = (amount) => {
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) throw new Error('Unverified money');
    const [whole, fraction = ''] = amount.split('.');
    const value = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    if (!Number.isSafeInteger(value)) throw new Error('Unverified money');
    return value;
  };
  const toLine = (item) => ({
    merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
    quantity: item.quantity,
    attributes: Object.entries(item.properties || {}).map(([key, value]) => ({ key, value: String(value) })),
    ...(item.selling_plan_allocation ? {
      sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}`,
    } : {}),
  });
  const quoteQuery = `mutation VitaBlueAovQuote($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
    cartCreate(input: $input) {
      cart {
        lines(first: 50) {
          nodes {
            quantity
            attributes { key value }
            merchandise { ... on ProductVariant { id availableForSale } }
            cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
            discountAllocations { discountedAmount { amount currencyCode } }
          }
        }
      }
      userErrors { field message }
    }
  }`;
  const quotes = new Map();
  const quote = (cart, selection, lines) => {
    const codes = (cart.discount_codes || []).filter((code) => code.applicable).map((code) => code.code);
    const input = {
      buyerIdentity: { countryCode: selection.country }, lines, discountCodes: codes,
      attributes: Object.entries(cart.attributes || {}).map(([key, value]) => ({ key, value: String(value) })),
    };
    const key = JSON.stringify([selection.country, selection.currency, input]);
    if (!quotes.has(key)) {
      if (quotes.size >= 12) quotes.delete(quotes.keys().next().value);
      quotes.set(key, fetch('/api/2026-10/graphql.json', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(8000),
        body: JSON.stringify({ query: quoteQuery, variables: { country: selection.country, input } }),
      }).then((response) => {
        if (!response.ok) throw new Error('Quote unavailable');
        return response.json();
      }).then((response) => {
        const result = response.data?.cartCreate;
        if (response.errors?.length || result?.userErrors?.length || !result?.cart) throw new Error('Quote unavailable');
        const items = result.cart.lines.nodes;
        if (items.length !== lines.length || items.some((item) => !item.merchandise.availableForSale ||
            item.cost.totalAmount.currencyCode !== selection.currency)) throw new Error('Unverified quote');
        return { items, subtotal: items.reduce((sum, item) => sum + money(item.cost.totalAmount.amount), 0) };
      }).catch((error) => {
        quotes.delete(key);
        throw error;
      }));
    }
    return quotes.get(key);
  };

  const appData = new Map();
  const getBundle = async (id, url) => {
    let source = window.__appDataMetaobject;
    if (!source?.[`bundle_${id}`]) {
      if (!appData.has(url)) appData.set(url, fetch(url, { signal: AbortSignal.timeout(8000) })
        .then((response) => {
          if (!response.ok) throw new Error('Rapi unavailable');
          return response.text();
        }).then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          for (const script of doc.scripts) {
            const match = script.textContent.match(/var __appDataMetaobject = (\{[^\n]+\});/);
            const data = match && parse(match[1]);
            if (data) return data;
          }
          throw new Error('Rapi unavailable');
        }).catch((error) => { appData.delete(url); throw error; }));
      source = await appData.get(url);
    }
    const bundle = parse(source[`bundle_${id}`]);
    if (!bundle?.is_published || bundle.is_archived || bundle.is_ab_test_active || bundle.is_freezed) {
      throw new Error('Unverified Rapi bundle');
    }
    return bundle;
  };

  let action;
  let cartRead;
  const refresh = () => {
    if (!cartRead) cartRead = readCart().then((cart) => {
      document.querySelectorAll('vitablue-cart-aov').forEach((element) => element.sync(cart));
      return cart;
    }).finally(() => { cartRead = undefined; });
    return cartRead;
  };

  customElements.define('vitablue-cart-aov', class extends HTMLElement {
    connectedCallback() {
      this.copy = parse(this.querySelector('[data-aov-copy]').textContent);
      this.products = parse(this.querySelector('[data-aov-products]').textContent);
      this.onClick = (event) => {
        const button = event.target.closest('[data-aov-action]');
        if (button && !button.disabled) this.activate(button.dataset.aovAction);
      };
      this.onShipping = (event) => {
        const shipping = event.detail;
        if (this.state && shipping.status === 'ready' && shipping.country === this.state.selection.country &&
            shipping.currency === this.state.cart.currency && shipping.subtotal === this.state.cart.total_price) {
          this.state.shipping = shipping;
          this.render();
        }
      };
      this.addEventListener('click', this.onClick);
      window.addEventListener('vitablue:shipping-update', this.onShipping);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cart = event.cartData;
        if (Number.isSafeInteger(cart?.total_price) && cart.currency && Array.isArray(cart.items)) this.sync(cart);
        else refresh().catch(() => { this.hidden = true; });
      });
      this.onError = subscribe(PUB_SUB_EVENTS.cartError, () => {
        action = undefined;
        this.showError();
      });
      refresh().catch(() => { this.hidden = true; });
    }

    disconnectedCallback() {
      this.revision = (this.revision || 0) + 1;
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('vitablue:shipping-update', this.onShipping);
      this.unsubscribe?.();
      this.onError?.();
    }

    derive(cart, selection) {
      if (!selection || cart.currency !== selection.currency || cart.items.length > 50) return null;
      const products = Object.values(this.products);
      const parents = cart.items.filter((item) => products.some((product) => String(item.product_id) === String(product.id)) &&
        metadata(item).main && !metadata(item).gift);
      if (parents.length !== 1) return null;
      const parent = parents[0], offer = metadata(parent);
      if (!parent.quantity || !offer.id || !offer.offer || !parent.properties._rapi_bundles ||
          parent.properties._rapi_market !== selection.country) return null;
      const product = products.find((product) => String(product.id) === String(parent.product_id));
      const crossProduct = products.find((candidate) => candidate !== product);
      const crossLines = cart.items.filter((item) => String(item.product_id) === String(crossProduct.id));
      const gift = crossLines.filter((item) => metadata(item).gift && item.final_line_price === 0 &&
        metadata(item).id === offer.id && String(item.properties._rapi_bundles) === String(parent.properties._rapi_bundles));
      return {
        cart, selection, parent, offer, gift, product, crossProduct,
        subscribed: !!parent.selling_plan_allocation,
        paidCrossSell: crossLines.some((item) => !metadata(item).gift || item.final_line_price > 0),
      };
    }

    async sync(cart) {
      const current = this.revision = (this.revision || 0) + 1;
      const snapshot = this.closest('cart-drawer')?.querySelector('[data-vb-shipping-context]');
      const selection = snapshot && parse(snapshot.textContent);
      const state = this.derive(cart, selection);
      this.hidden = true;
      this.state = undefined;
      if (!state) return;
      try {
        const bundle = await getBundle(state.parent.properties._rapi_bundles, state.product.url);
        if (!bundle.selected_products.some((product) => String(product.shopify_product_id) === String(state.product.id)) ||
            (bundle.target_markets?.length && !bundle.target_markets.includes(selection.country))) return;
        const tier = bundle.offers.find((offer) => Number(offer.quantity) === Math.min(3, state.parent.quantity));
        if (!tier || !bundle.offers.some((offer) => offer.identifier_id === state.offer.offer)) return;
        const double = bundle.offers.find((offer) => Number(offer.quantity) === 2);
        const trifecta = bundle.offers.find((offer) => Number(offer.quantity) === 3);
        if (double?.discount_type !== 'percentage' || Number(double.discount_value) !== 10 ||
            trifecta?.discount_type !== 'percentage' || Number(trifecta.discount_value) !== 15) return;
        state.bundle = bundle;
        state.tier = tier;
        state.trifecta = trifecta;
        if (action?.kind === 'upgrade' && state.parent.quantity === action.quantity) action = undefined;
        state.shipping = await window.VitaBlueShipping.resolve({ ...selection, subtotal: cart.total_price, items: cart.items });
        state.crossSell = !state.subscribed && !state.paidCrossSell && state.parent.quantity !== 2 ?
          tier.offer_upsells?.find((upsell) => String(upsell.shopify_product_id) === String(state.crossProduct.id) &&
            Number(upsell.quantity) === 1 && upsell.discount_type === 'percentage' &&
            Number(upsell.discount_value) === (state.parent.quantity === 1 ? 25 : 50)) : null;
        state.crossPrice = null;
        state.upgradeShipping = false;
        state.quoteStatus = 'pending';
        if (current !== this.revision || !this.isConnected) return;
        this.state = state;
        this.render();
        if (state.crossSell || state.parent.quantity === 2) {
          try {
            const lines = cart.items.map(toLine);
            if (state.crossSell) {
              const candidate = this.crossLine(state);
              const result = await quote(cart, selection, [...lines, toLine(candidate)]);
              const item = result.items.find((item) => {
                const properties = Object.fromEntries(item.attributes.map(({ key, value }) => [key, value]));
                const offer = parse(properties.__rapi_offer);
                return offer?.upsell && offer.upid === state.crossSell.identifier_id && offer.id === state.offer.id;
              });
              if (!item || !item.discountAllocations.length ||
                  money(item.cost.totalAmount.amount) >= money(item.cost.subtotalAmount.amount)) throw new Error('Undiscounted upsell');
              state.crossPrice = money(item.cost.totalAmount.amount);
              state.crossOriginalPrice = money(item.cost.subtotalAmount.amount);
            } else {
              const baseline = await quote(cart, selection, lines);
              if (baseline.subtotal !== cart.total_price) throw new Error('Quote differs from actual cart');
              const upgraded = cart.items.filter((item) => !state.gift.includes(item)).map((item) => item === state.parent ? {
                ...item, quantity: 3,
              } : item);
              const gift = trifecta.free_gifts?.find((gift) => String(gift.shopify_product_id) === String(state.crossProduct.id) && Number(gift.quantity) === 1);
              if (!gift) throw new Error('Unverified gift');
              const quantityQuote = await quote(cart, selection, upgraded.map(toLine));
              const upgradedParent = upgraded.find((item) => item === state.parent || item.key === state.parent.key);
              upgradedParent.properties = {
                ...upgradedParent.properties,
                __rapi_offer: JSON.stringify({ ...state.offer, offer: trifecta.identifier_id }),
              };
              upgraded.push({
                product_id: Number(state.crossProduct.id), requires_shipping: true,
                variant_id: Number(state.crossProduct.variant), quantity: 1,
                properties: {
                  _rapi_bundles: state.parent.properties._rapi_bundles, _rapi_market: selection.country,
                  __rapi_offer: JSON.stringify({ id: state.offer.id, offer: trifecta.identifier_id, gift: true }),
                },
              });
              const result = await quote(cart, selection, upgraded.map(toLine));
              const quotedGift = result.items.find((item) => metadata({ properties: Object.fromEntries(item.attributes.map(({ key, value }) => [key, value])) }).gift);
              if (!quotedGift || money(quotedGift.cost.totalAmount.amount) !== 0) throw new Error('Unverified gift price');
              state.upgradeSubtotal = Math.min(quantityQuote.subtotal, result.subtotal);
              const shipping = await window.VitaBlueShipping.resolve({ ...selection, subtotal: state.upgradeSubtotal, items: upgraded });
              state.upgradeShipping = shipping.status === 'ready' && shipping.unlocked;
            }
            state.quoteStatus = 'ready';
          } catch (error) {
            state.crossPrice = null;
            state.upgradeShipping = false;
            state.quoteStatus = error.message;
          }
          if (current === this.revision && this.isConnected) this.render();
        }
      } catch {
        if (current === this.revision) this.hidden = true;
      }
    }

    crossLine(state) {
      if (state.subscribed || state.paidCrossSell || !state.crossSell) throw new Error('Paid cross-sell not permitted');
      const properties = {
        _rapi_bundles: state.parent.properties._rapi_bundles,
        __rapi_offer: JSON.stringify({ id: state.offer.id, offer: state.tier.identifier_id, upsell: true, upid: state.crossSell.identifier_id }),
        _rapi_market: state.parent.properties._rapi_market,
      };
      if (state.parent.properties._rapi_session_id) properties._rapi_session_id = state.parent.properties._rapi_session_id;
      return { variant_id: Number(state.crossProduct.variant), quantity: 1, properties };
    }

    render() {
      const state = this.state, qty = state.parent.quantity;
      const copy = state.product.kind === 'nano' ? { ...this.copy,
        trifecta_copy: this.copy.nano_trifecta_copy, gift: this.copy.nano_gift,
        another_copy: this.copy.another_dropper_copy, another_compact: this.copy.another_dropper_compact,
      } : this.copy;
      for (const [selector, product] of [
        ['[data-aov-parent-image]', state.product], ['[data-aov-gift-image]', state.crossProduct],
        ['[data-aov-cross-image]', state.crossProduct],
      ]) {
        const image = this.querySelector(selector);
        if (image) {
          const source = selector === '[data-aov-gift-image]' ? product.giftImage : product.image;
          image.hidden = !source;
          if (source && image.getAttribute('src') !== source) image.src = source;
          image.alt = product.title;
        }
      }
      const main = this.querySelector('[data-aov-main]');
      const addon = this.querySelector('[data-aov-addon]');
      const upgrade = this.querySelector('[data-aov-action="upgrade"]');
      const benefits = this.querySelector('[data-aov-benefits]');
      this.dataset.presentation = qty === 1 ? 'double' : qty === 2 ? 'hero' : 'success';
      this.querySelector('[data-aov-eyebrow]').textContent = qty === 1 ? 'SAVE MORE' : qty === 2 ? 'BEST NEXT STEP' : '';
      this.querySelector('[data-aov-badge]').textContent = qty === 1 ? '25% OFF' : '50% OFF';
      const compare = this.querySelector('[data-aov-compare]');
      compare.textContent = state.crossOriginalPrice == null ? '' : new Intl.NumberFormat(state.selection.locale, { style: 'currency', currency: state.cart.currency }).format(state.crossOriginalPrice / 100);
      main.hidden = qty >= 4;
      addon.hidden = state.subscribed || state.paidCrossSell || state.crossPrice === null;
      this.hidden = main.hidden && addon.hidden;
      this.querySelector('[data-aov-heading]').textContent = qty === 1 ?
        (state.subscribed ? copy.double_subscription : 'Double Up 💙') : qty === 2 ? 'Unlock The Trifecta 🔱' : '✓ BEST VALUE UNLOCKED';
      const description = this.querySelector('[data-aov-description]');
      description.textContent = qty === 1 && !state.subscribed ? copy.double_copy : qty === 2 ? copy.trifecta_copy : '';
      description.hidden = !description.textContent;
      const perks = [];
      if (qty === 2) perks.push(copy.savings, copy.gift, ...(state.upgradeShipping ? [copy.shipping] : []));
      if (qty === 3) {
        const discounted = state.parent.line_level_discount_allocations?.some((allocation) => allocation.amount > 0 &&
          (allocation.discount_application.title === state.tier.title ||
            allocation.discount_application.value_type === 'percentage' && Number(allocation.discount_application.value) === 15));
        if (discounted) perks.push(copy.savings);
        if (state.gift.length === 1) perks.push(copy.gift);
        if (state.shipping.status === 'ready' && state.shipping.unlocked) perks.push(copy.shipping);
      }
      benefits.replaceChildren(...perks.map((text) => {
        const item = document.createElement('li');
        item.textContent = `✓ ${text}`;
        return item;
      }));
      benefits.hidden = !perks.length;
      upgrade.hidden = qty >= 3;
      upgrade.textContent = qty === 1 ? copy.add_one : copy.unlock;
      this.querySelector('[data-aov-addon-heading]').textContent = qty === 1 ? (state.product.kind === 'nano' ? this.copy.dropper : 'Add a Nano') :
        qty >= 4 ? copy.another_compact : state.product.kind === 'nano' ? this.copy.another_dropper : 'Want another Nano?';
      const addonDescription = this.querySelector('[data-aov-addon-description]');
      addonDescription.textContent = qty === 3 ? copy.another_copy : '';
      addonDescription.hidden = !addonDescription.textContent;
      this.querySelector('[data-aov-price]').textContent = state.crossPrice === null ? '' :
        new Intl.NumberFormat(state.selection.locale, { style: 'currency', currency: state.cart.currency }).format(state.crossPrice / 100);
      this.querySelector('[data-aov-action="cross-sell"]').textContent = qty === 1 ? '+ ADD' : state.product.kind === 'nano' ? this.copy.add_dropper : '+ ADD NANO';
      this.querySelectorAll('[data-aov-action]').forEach((button) => { button.disabled = !!action; });
      this.setAttribute('aria-busy', String(!!action));
    }

    showError() {
      const node = this.querySelector('[data-aov-error]');
      if (!node) return;
      node.textContent = this.copy.error;
      node.hidden = false;
      this.querySelectorAll('[data-aov-action]').forEach((button) => { button.disabled = false; });
      this.setAttribute('aria-busy', 'false');
    }

    async activate(kind) {
      if (action || !this.state) return;
      const expected = this.state;
      action = { kind };
      this.querySelector('[data-aov-error]').hidden = true;
      this.render();
      try {
        const cart = await readCart();
        if (signature(cart) !== signature(expected.cart)) {
          action = undefined;
          await this.sync(cart);
          return;
        }
        const state = this.derive(cart, expected.selection);
        if (!state || state.parent.key !== expected.parent.key) throw new Error('Cart changed');
        if (kind === 'upgrade') {
          if (state.parent.quantity > 2) throw new Error('Upgrade not permitted');
          const input = [...this.closest('cart-drawer').querySelectorAll('.quantity__input')].find((input) =>
            Number(input.dataset.index) === cart.items.indexOf(state.parent) + 1 && Number(input.value) === state.parent.quantity);
          const plus = input?.closest('quantity-input')?.querySelector('[name="plus"]');
          if (!plus || plus.disabled || plus.classList.contains('disabled')) throw new Error('Quantity unavailable');
          action = { kind, quantity: state.parent.quantity + 1 };
          plus.click();
          setTimeout(() => {
            if (action?.kind === kind && action.quantity === state.parent.quantity + 1) {
              action = undefined;
              refresh().catch(() => {});
            }
          }, 10000);
          return;
        }
        if (kind !== 'cross-sell' || state.subscribed || state.paidCrossSell || expected.crossPrice === null) throw new Error('Paid cross-sell not permitted');
        const candidate = this.crossLine(expected);
        const drawer = this.closest('cart-drawer');
        drawer.setActiveElement(document.activeElement);
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: candidate.variant_id, quantity: 1, properties: candidate.properties }],
            sections: drawer.getSectionsToRender().map((section) => section.id), sections_url: location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('Add unavailable');
        const updated = await readCart();
        const crossSell = updated.items.find((item) => metadata(item).upsell && metadata(item).upid === expected.crossSell.identifier_id &&
          metadata(item).id === expected.offer.id && String(item.product_id) === String(state.crossProduct.id));
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vitablue-cart-aov', cartData: updated });
        drawer.renderContents(result);
        if (!crossSell || crossSell.quantity !== 1 || crossSell.selling_plan_allocation || crossSell.final_line_price !== expected.crossPrice) {
          throw new Error('Upsell differs from verified quote');
        }
      } catch {
        const current = document.querySelector('vitablue-cart-aov');
        current?.showError();
      } finally {
        if (action?.kind !== 'upgrade') {
          action = undefined;
          refresh().catch(() => {});
        }
      }
    }
  });

  if (typeof PerformanceObserver !== 'undefined') {
    let queued = false, running = false;
    new PerformanceObserver((records) => {
      if (!records.getEntries().some((entry) => {
        const url = new URL(entry.name);
        return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
      })) return;
      queued = true;
      if (running) return;
      running = true;
      queueMicrotask(async () => {
        while (queued) {
          queued = false;
          await refresh().catch(() => {});
        }
        running = false;
      });
    }).observe({ type: 'resource' });
  }
})();
